import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, ExternalLink, MessageCircle, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";

// Widget "Link de cadastro do cliente" — dentro da aba Clientes.
// Gera o link público /onboard?v={vendedor} para o admin/vendedora enviar ao cliente.
export default function OnboardingLinkCard() {
  const { user } = useAuth();
  const meuNome = (user?.vendedor_name || user?.name || "").trim();
  const [copied, setCopied] = useState(false);

  const link = useMemo(() => {
    const base = window.location.origin;
    const qs = meuNome ? `?v=${encodeURIComponent(meuNome)}` : "";
    return `${base}/onboard${qs}`;
  }, [meuNome]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiado! Cole no WhatsApp ou e-mail do cliente.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não consegui copiar — selecione manualmente");
    }
  };

  const abrirWhatsApp = () => {
    const texto = encodeURIComponent(
      `Olá! Segue o link para cadastro do seu plano FLUA. Preencha seus dados e nossa equipe entra em contato para agendar a instalação:\n\n${link}`
    );
    window.open(`https://wa.me/?text=${texto}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 space-y-3"
      data-testid="onboarding-link-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-base">Link de auto-cadastro do cliente</h3>
            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/40">
              Público
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Envie o link pelo WhatsApp/e-mail. O cliente preenche os dados sozinho, escolhe um dos 4 planos e cai na fila abaixo — <strong className="text-amber-500">você tem 30 min pra validar e enviar o link de assinatura</strong>.
          </p>
        </div>
        {meuNome && (
          <Badge variant="outline" className="text-[10px]">
            Atribuído a: <span className="ml-1 font-semibold">{meuNome}</span>
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0 relative">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
            className="pl-9 font-mono text-xs"
            data-testid="onboarding-link-input"
          />
        </div>
        <Button onClick={copiar} size="sm" data-testid="btn-copiar-link" className="shrink-0">
          {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copiado</> : <><Copy className="w-4 h-4 mr-1.5" /> Copiar link</>}
        </Button>
        <Button variant="outline" size="sm" onClick={abrirWhatsApp} data-testid="btn-enviar-whatsapp"
          className="shrink-0 text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10">
          <MessageCircle className="w-4 h-4 mr-1.5" /> Enviar por WhatsApp
        </Button>
        <a href={link} target="_blank" rel="noopener noreferrer"
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 shrink-0"
          data-testid="btn-preview-link">
          <ExternalLink className="w-3.5 h-3.5" /> Visualizar
        </a>
      </div>

      {!meuNome && (
        <p className="text-[11px] text-amber-500">
          Preencha seu "nome de vendedor(a)" em Admin → Usuários para o link atribuir os cadastros a você automaticamente.
        </p>
      )}
    </div>
  );
}
